import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { ROLES } from '../constants/roles';
import { 
  migrateExistingUsers, 
  dryRunMigration, 
  checkMigrationStatus 
} from '../utils/migrateExistingUsers';

export default function MigrationUtility() {
  const { userDoc } = useAuth();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [operation, setOperation] = useState('');

  const handleCheckStatus = async () => {
    setLoading(true);
    setOperation('Checking migration status...');
    try {
      const result = await checkMigrationStatus();
      setResults({
        type: 'status',
        data: result
      });
    } catch (error) {
      setResults({
        type: 'error',
        data: { message: error.message }
      });
    } finally {
      setLoading(false);
      setOperation('');
    }
  };

  const handleDryRun = async () => {
    setLoading(true);
    setOperation('Running dry run migration...');
    try {
      const result = await dryRunMigration();
      setResults({
        type: 'dryrun',
        data: result
      });
    } catch (error) {
      setResults({
        type: 'error',
        data: { message: error.message }
      });
    } finally {
      setLoading(false);
      setOperation('');
    }
  };

  const handleMigrate = async () => {
    if (!window.confirm('Are you sure you want to run the migration? This will update user documents in the database.')) {
      return;
    }

    setLoading(true);
    setOperation('Running migration...');
    try {
      const result = await migrateExistingUsers();
      setResults({
        type: 'migration',
        data: result
      });
    } catch (error) {
      setResults({
        type: 'error',
        data: { message: error.message }
      });
    } finally {
      setLoading(false);
      setOperation('');
    }
  };

  if (!userDoc || userDoc.role !== ROLES.MASTER) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">This page is only accessible to master administrators.</p>
          <Link to="/" className="text-blue-600 hover:underline mt-4 block">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-sky-600 text-white p-6">
        <div className="container mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">🔧 Migration Utility</h1>
              <p className="text-blue-200">Migrate existing users to the new pending-approval system</p>
            </div>
            <Link 
              to="/master-admin" 
              className="bg-blue-700 hover:bg-blue-800 px-4 py-2 rounded-lg transition duration-200"
            >
              ← Back to Admin
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Warning Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-yellow-900">⚠️ Important Migration Notice</h3>
              <div className="mt-2 text-yellow-800 space-y-2">
                <p>This utility migrates existing users to the new pending-approval system:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li><strong>All existing users will be set to ACTIVE status</strong> (approved automatically)</li>
                  <li><strong>New users after migration will start as PENDING</strong> and require approval</li>
                  <li><strong>This should only be run ONCE</strong> before public deployment</li>
                  <li>Always run a <strong>DRY RUN</strong> first to see what changes will be made</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Operation Buttons */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Migration Operations</h2>
          
          <div className="grid md:grid-cols-3 gap-4">
            <button
              onClick={handleCheckStatus}
              disabled={loading}
              className="flex flex-col items-center p-4 border border-blue-200 rounded-lg hover:bg-blue-50 transition duration-200 disabled:opacity-50"
            >
              <div className="text-2xl mb-2">🔍</div>
              <h3 className="font-medium text-blue-900">Check Status</h3>
              <p className="text-sm text-blue-700 text-center mt-1">
                Check if migration is needed
              </p>
            </button>

            <button
              onClick={handleDryRun}
              disabled={loading}
              className="flex flex-col items-center p-4 border border-yellow-200 rounded-lg hover:bg-yellow-50 transition duration-200 disabled:opacity-50"
            >
              <div className="text-2xl mb-2">🧪</div>
              <h3 className="font-medium text-yellow-900">Dry Run</h3>
              <p className="text-sm text-yellow-700 text-center mt-1">
                Preview changes without applying
              </p>
            </button>

            <button
              onClick={handleMigrate}
              disabled={loading}
              className="flex flex-col items-center p-4 border border-red-200 rounded-lg hover:bg-red-50 transition duration-200 disabled:opacity-50"
            >
              <div className="text-2xl mb-2">🚀</div>
              <h3 className="font-medium text-red-900">Run Migration</h3>
              <p className="text-sm text-red-700 text-center mt-1">
                Apply changes to database
              </p>
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
              <span className="text-blue-800">{operation}</span>
            </div>
          </div>
        )}

        {/* Results Display */}
        {results && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {results.type === 'status' && '📊 Migration Status'}
              {results.type === 'dryrun' && '🧪 Dry Run Results'}
              {results.type === 'migration' && '🚀 Migration Results'}
              {results.type === 'error' && '❌ Error'}
            </h2>

            {results.type === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800">{results.data.message}</p>
              </div>
            )}

            {results.type === 'status' && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-600">{results.data.totalUsers}</div>
                    <div className="text-blue-800">Total Users</div>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-yellow-600">{results.data.usersWithoutStatus}</div>
                    <div className="text-yellow-800">Need Migration</div>
                  </div>
                  <div className={`p-4 rounded-lg text-center ${results.data.migrationNeeded ? 'bg-red-50' : 'bg-green-50'}`}>
                    <div className={`text-2xl font-bold ${results.data.migrationNeeded ? 'text-red-600' : 'text-green-600'}`}>
                      {results.data.migrationNeeded ? 'YES' : 'NO'}
                    </div>
                    <div className={results.data.migrationNeeded ? 'text-red-800' : 'text-green-800'}>
                      Migration Needed
                    </div>
                  </div>
                </div>
              </div>
            )}

            {results.type === 'dryrun' && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-600">{results.data.wouldProcess}</div>
                    <div className="text-blue-800">Would Process</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-600">{results.data.wouldUpdate}</div>
                    <div className="text-green-800">Would Update</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-gray-600">{results.data.wouldSkip}</div>
                    <div className="text-gray-800">Would Skip</div>
                  </div>
                </div>
                
                <div className="mt-6">
                  <details className="border border-gray-200 rounded-lg">
                    <summary className="p-4 cursor-pointer font-medium text-gray-800 hover:bg-gray-50">
                      View Detailed Changes ({results.data.details.length} users)
                    </summary>
                    <div className="border-t border-gray-200 p-4">
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {results.data.details.map((detail, index) => (
                          <div key={index} className="text-sm border-b border-gray-100 pb-2">
                            <div className="font-medium">{detail.email}</div>
                            <div className="text-gray-600">
                              Current: {detail.currentRole || 'none'}/{detail.currentStatus || 'none'} → 
                              Action: {detail.action}
                              {detail.proposedUpdates && (
                                <span> → New: {detail.proposedUpdates.role}/{detail.proposedUpdates.status}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </details>
                </div>
              </div>
            )}

            {results.type === 'migration' && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-600">{results.data.processed}</div>
                    <div className="text-blue-800">Processed</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-600">{results.data.updated}</div>
                    <div className="text-green-800">Updated</div>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-red-600">{results.data.errors}</div>
                    <div className="text-red-800">Errors</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-gray-600">
                      {results.data.processed - results.data.updated - results.data.errors}
                    </div>
                    <div className="text-gray-800">Skipped</div>
                  </div>
                </div>

                {results.data.updated > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="font-medium text-green-900 mb-2">✅ Migration Completed Successfully!</h3>
                    <p className="text-green-800">
                      {results.data.updated} users have been migrated to the new status system. 
                      All existing users are now active and new registrations will require approval.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}